import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  addActionAssignee,
  changeActionStatus,
  createSubAction,
  deleteActionAttachment,
  deleteActionNote,
  deleteSubAction,
  getActionDetail,
  getAllowedTransitions,
  removeActionAssignee,
  setMainResponsible,
  setSubActionAssignee,
  updateAction,
  updateActionNote,
  updateSubAction,
  uploadActionAttachments,
} from '../api/actionApi'
import type { AppFeedbackTriggerKind } from '../../../lib/appFeedback/trigger'
import type {
  ActionDetail,
  ActionUpdateRequest,
  PersonInput,
  SubActionCreateRequest,
  SubActionUpdateRequest,
} from '../types'

/**
 * Detay sorgularının <b>öneki</b> — yalnız {@code invalidateQueries} için. Dil parçasını taşımaz, bu yüzden
 * önek eşlemesiyle her iki dilin girdisini birden geçersizleştirir (istenen davranış).
 * {@code setQueryData} bunu KULLANAMAZ; oraya {@link useActionDetailKey}'in verdiği tam anahtar gider.
 */
const KEY = (id: number) => ['action-detail', id] as const

/**
 * Okunan/yazılan <b>tam</b> detay anahtarı: önek + aktif dil. Yanıttaki REFERANS adları ({@code statusName},
 * seviye adları…) sunucuda {@code Accept-Language}'e göre çözülür, yani kayıt dile özgüdür.
 */
function useActionDetailKey(id: number) {
  const { i18n } = useTranslation()
  return [...KEY(id), i18n.language] as const
}

/**
 * Aksiyon detayı (analiz 7).
 *
 * <p>Aktif dil key'e girer: dil TR↔EN değiştiğinde durum/seviye adları eski dilde kalmasın
 * ({@code useActions} ile aynı kalıp; toplantı detayında yaşanan bulgunun aynısı).
 */
export function useActionDetail(id: number | null) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['action-detail', id, i18n.language],
    queryFn: () => getActionDetail(id as number),
    enabled: id != null,
  })
}

/** Tanım/açıklama/termin + opsiyonel not (yalnız metin) güncelle → dönen taze detayı cache'e yaz. */
export function useUpdateAction(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: (body: ActionUpdateRequest) => updateAction(id, body),
    onSuccess: (fresh: ActionDetail) => qc.setQueryData(detailKey, fresh),
  })
}

/** Aksiyon notunu düzenle (yalnız metin) → dönen taze detayı cache'e yaz. ACTION_NOTE_MANAGE. */
export function useUpdateActionNote(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: ({ noteId, note, modDate }: { noteId: number; note: string; modDate: string }) =>
      updateActionNote(id, noteId, note, modDate),
    onSuccess: (fresh: ActionDetail) => qc.setQueryData(detailKey, fresh),
  })
}

/** Aksiyon notunu sil (soft-delete) → dönen taze detayı cache'e yaz. ACTION_NOTE_MANAGE. */
export function useDeleteActionNote(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: (noteId: number) => deleteActionNote(id, noteId),
    onSuccess: (fresh: ActionDetail) => qc.setQueryData(detailKey, fresh),
  })
}

/** Aksiyona doküman(lar) ekle (ACTION_FILE) → detayı tazele. */
export function useUploadAttachments(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (files: File[]) => uploadActionAttachments(id, files),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(id) }),
  })
}

/** Aksiyondan doküman ekini kaldır (soft-delete) → detayı tazele. */
export function useDeleteAttachment(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (attachmentId: number) => deleteActionAttachment(id, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(id) }),
  })
}

/** Statü değiştir (204) → detayı + izinli geçişleri tazele. */
export function useChangeActionStatus(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      toStatusId,
      modDate,
      newDeadline,
      note,
      completionNote,
    }: {
      toStatusId: number
      modDate: string
      newDeadline?: string | null
      note?: string | null
      /** 105 — tamamlanma notu; yalnız hedef Tamamlandı(331) iken anlamlıdır. */
      completionNote?: string | null
    }) => changeActionStatus(id, toStatusId, modDate, newDeadline, note, completionNote),
    // Doğrulama (400) hatalarını ekran üstlenir (statusChangeErrorKey ile açıklayıcı mesaj); merkez jenerik
    // toast basmasın (çift bildirimi önler). 403/409 vb. yine merkezî bildirime düşer.
    // appFeedback (096): bu hook HER statü geçişinde çalışır; anket YALNIZ hedef Tamamlandı(331)
    // iken tetiklenir. Koşul meta'da ifade edilemez (meta hook başına sabittir) — hedef statü
    // trigger.ts içinde `variables.toStatusId` üzerinden okunur.
    meta: {
      handledCodes: ['VALIDATION_ERROR'],
      appFeedback: 'status' satisfies AppFeedbackTriggerKind,
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(id) })
      qc.invalidateQueries({ queryKey: ['action-transitions', id] })
      // Statü (ana veya alt aksiyon) değişince Toplantı Detay'daki aksiyon tablosu da bayatlar → tazele. Bu
      // hook meetingId bilmez; tüm meeting-detail sorguları geçersizleştirilir (pratikte cache'te tek toplantı).
      // Alt aksiyonda ana aksiyonun türetilen durumu değiştiği için de gereklidir (066/AK-6).
      qc.invalidateQueries({ queryKey: ['meeting-detail'] })
    },
  })
}

/**
 * Yan Sorumlu ekle → detayı tazele (izinli geçişler de tazelenir: ekleme otomatik alt aksiyon üretir).
 *
 * <p><b>Geciken aksiyonda uç reddeder</b> (400 `overdueAssignee`, kullanıcı kuralı 2026-08-17) — önce
 * hedef teslim tarihi güncellenir. ~~066/AK-12: Gecikti ise yeni termin zorunlu (`deadlineRequired`),
 * verilince ana termin güncellenir~~ o dal kaldırıldı; bu yüzden mutation gövdesi artık yalnız kişi
 * taşır (aşağıdaki yoruma bakın).
 *
 * <p><b>Çağıran {@code onError} vermek ZORUNDADIR</b> — {@link useCreateSubAction}'daki örtük sözleşmenin
 * aynısı: {@code meta.handledCodes} doğrulama (400) hatalarında merkezî bildirimi susturur, mesajı
 * göstermek ekranın işidir ({@code assigneeAddErrorKey}).
 */
export function useAddAssignee(id: number) {
  const qc = useQueryClient()
  return useMutation({
    // `newDeadline`/`note` KALDIRILDI (2026-08-17): geciken aksiyona Yan Sorumlu eklenemediği için
    // (uç 400 `overdueAssignee`) sorumlu ekleme ucu artık termin/gerekçe almıyor — bkz. AssigneeAddRequest.
    mutationFn: ({ person }: { person: PersonInput }) => addActionAssignee(id, person),
    // Doğrulama (400) hatalarını ekran üstlenir (assigneeAddErrorKey — tooManyAssignees / overdueAssignee /
    // mükerrer sicil); merkez jenerik toast basmasın (çift bildirimi önler). useCreateSubAction ile aynı desen.
    meta: { handledCodes: ['VALIDATION_ERROR'] },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(id) })
      qc.invalidateQueries({ queryKey: ['action-transitions', id] })
    },
  })
}

/**
 * Sorumlu çıkar → detayı tazele. Ekleme yoluyla aynı sözleşme: 400'ü ekran üstlenir
 * ({@code assigneeRemoveErrorKey} — `atLeastOneAssignee`), merkez susar; **{@code onError} zorunludur.**
 */
export function useRemoveAssignee(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (employeeId: string) => removeActionAssignee(id, employeeId),
    meta: { handledCodes: ['VALIDATION_ERROR'] },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(id) }),
  })
}

/** Ana Sorumlu ata/değiştir → dönen taze detayı cache'e yaz. */
export function useSetMainResponsible(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: (person: PersonInput) => setMainResponsible(id, person),
    onSuccess: (fresh: ActionDetail) => qc.setQueryData(detailKey, fresh),
  })
}

export interface SubActionCreateArgs {
  body: SubActionCreateRequest
  /** Oluşturma anında seçilen dokümanlar. Boş/verilmemiş olabilir — ek isteğe bağlıdır. */
  files?: File[]
}

export interface SubActionCreateResult {
  /** Uçtan dönen taze ÜST görev detayı (alt görev listesi dâhil). */
  detail: ActionDetail
  /** Bu istekle açılan alt görev sayısı (sorumlu başına bir — 20.10.2026). Bildirim metni için. */
  createdCount: number
  /**
   * Alt görev(ler) oluştu ama ek(ler) en az birine yüklenemedi. Çağıran başarı yerine <b>uyarı</b>
   * gösterir.
   */
  filesFailed: boolean
}

/**
 * Yeni açılan alt görevlerin id'lerini bulur: çağrı <b>öncesi</b> ve <b>sonrası</b> alt görev
 * kimliklerinin farkı.
 *
 * <p><b>Neden fark alınıyor:</b> {@code POST /{id}/subactions} alt görevlerin kendisini değil
 * <b>üst görevin</b> güncel detayını döndürür ({@code ActionDetailService.createSubAction} →
 * {@code loadDetail(parentId)}), yani eklenen kayıtların id'si yanıtta doğrudan yoktur. Ek yükleme ucu
 * ({@code POST /actions/{id}/attachments}) o id'lere muhtaçtır.
 *
 * <p><b>Neden "en büyük id'ler" değil:</b> sequence monotonik olsa da, aradan başka bir kullanıcının
 * eklediği alt görev geçerse en büyük id'lerden biri <b>onunki</b> olur ve dosyalar yanlış göreve
 * iliştirilir. Fark tam olarak {@code expected} eleman vermiyorsa {@code null} döner ve çağıran dosyayı
 * <b>hiçbirine</b> yüklemez: yanlış kayda iliştirmektense yüklememek ve kullanıcıyı uyarmak yeğdir. Aynı
 * "sessiz eşleşme kaybına izin verme" disiplini tutanak tarafındaki {@code planPendingPrincipleUploads}'ta
 * da var.
 *
 * <p>Toplu oluşturmada (20.10.2026) {@code expected} istekteki sorumlu sayısıdır: sunucu sorumlu başına
 * tam bir alt görev açar.
 */
export function findCreatedSubActionIds(
  before: readonly { id: number }[],
  after: readonly { id: number }[],
  expected: number,
): number[] | null {
  const known = new Set(before.map((s) => s.id))
  const created = after.filter((s) => !known.has(s.id)).map((s) => s.id)
  return created.length === expected ? created : null
}

/**
 * Alt aksiyon oluştur → dönen taze detayı cache'e yaz; varsa <b>ekleri de yükle</b>.
 *
 * <p><b>Çağıran {@code onError} vermek ZORUNDADIR.</b> Bu mutation {@code meta.handledCodes} ile
 * doğrulama (400) hatalarında merkezî bildirimi <b>susturur</b> (çift toast olmasın diye); mesajı
 * göstermek artık ekranın sorumluluğundadır ({@code subActionCreateErrorKey}). {@code onError}
 * verilmezse doğrulama hatası <b>tamamen sessiz</b> kalır — ne merkez ne ekran bildirir.
 * Aynı örtük sözleşme {@link useUpdateSubAction} ve {@link useChangeActionStatus} için de geçerlidir.
 *
 * <p><b>Ek adımı (28.09.2026):</b> iki isteklidir — önce kayıt, sonra ekler; tek multipart uç yoktur.
 * Yeni kayıtların id'si {@link findCreatedSubActionIds} ile bulunur. <b>Ek adımının hatası YUTULUR</b>
 * ve {@code filesFailed} ile bildirilir; gerekçesi {@code useCreateStandaloneAction}'daki ile aynıdır —
 * reddedilseydi alt görev oluşmuş olmasına rağmen dialog açık kalır ve yeniden Kaydet <b>ikinci bir
 * alt görev</b> yaratırdı.
 *
 * <p><b>Toplu oluşturma (20.10.2026):</b> birden çok sorumlu seçildiyse sunucu her biri için bir alt
 * görev açar; seçilen dosyalar <b>her birine</b> ayrı ayrı yüklenir (her alt görev ayrı bir kayıttır,
 * ek paylaşımı yoktur). Yüklemeler <b>sırayla</b> yapılır: her istek seçilen dosyaların tamamını
 * taşıdığı için paralel gönderim (15 alt görev × tüm dosyalar) sunucuya ve bağlantıya aynı anda
 * yüklenirdi. Biri patlasa da diğerleri sürer; sonuç tek {@code filesFailed} bayrağında toplanır.
 */
export function useCreateSubAction(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: async ({ body, files = [] }: SubActionCreateArgs): Promise<SubActionCreateResult> => {
      const before = qc.getQueryData<ActionDetail>(detailKey)?.subActions ?? []
      const detail = await createSubAction(id, body)
      const createdCount = body.assignees.length
      if (files.length === 0) return { detail, createdCount, filesFailed: false }

      const createdIds = findCreatedSubActionIds(before, detail.subActions, createdCount)
      if (createdIds == null) return { detail, createdCount, filesFailed: true }
      let filesFailed = false
      for (const subId of createdIds) {
        try {
          await uploadActionAttachments(subId, files)
        } catch {
          filesFailed = true
        }
      }
      return { detail, createdCount, filesFailed }
    },
    // Doğrulama (400) hatalarını ekran üstlenir (subActionCreateErrorKey ile açıklayıcı mesaj —
    // parentOverdue / deadlineAfterParent); merkez jenerik toast basmasın (çift bildirimi önler).
    // 403/409 vb. yine merkezî bildirime düşer. useChangeActionStatus ile aynı desen.
    // proxyBlockedKey (089): vekil modunda alt aksiyon eklemek de yasaktır — 403 gelirse nedeni
    // söyleyen metin gösterilir (yalnız persona aktifken).
    // appFeedback (096): Alt Görev oluşturma da tetikleyicidir — ana Görev ile eşit davranır.
    meta: {
      handledCodes: ['VALIDATION_ERROR'],
      proxyBlockedKey: 'proxy.actionCreateBlocked',
      appFeedback: 'create' satisfies AppFeedbackTriggerKind,
    },
    onSuccess: ({ detail }: SubActionCreateResult) => qc.setQueryData(detailKey, detail),
  })
}

/**
 * Alt görev sil → dönen taze ÜST detayı cache'e yaz + (ana durum değişebilir) geçişleri tazele.
 *
 * <p>{@code handledCodes} (2026-09-17): 400 merkezî "bir hata oluştu" toast'ına düşmez — ekran nedeni
 * söyleyen metni gösterir ({@code subActionDeleteErrorKey}; otomatik alt görevde silme üst görevi
 * sorumlusuz bırakacaksa sunucu reddeder). **{@code onError} zorunludur**, aksi halde hata sessiz kalır.
 */
export function useDeleteSubAction(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: (subId: number) => deleteSubAction(subId),
    meta: { handledCodes: ['VALIDATION_ERROR'] },
    onSuccess: (fresh: ActionDetail) => {
      qc.setQueryData(detailKey, fresh)
      qc.invalidateQueries({ queryKey: ['action-transitions', id] })
    },
  })
}

/** Alt aksiyon alan güncelle (Tanım+Termin+Atanan) → dönen taze detayı cache'e yaz + geçişleri tazele. */
export function useUpdateSubAction(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: (body: SubActionUpdateRequest) => updateSubAction(id, body),
    // Oluşturma yoluyla aynı: 400'ü ekran üstlenir (subActionSaveErrorKey — deadlineAfterParent burada da
    // fırlatılır), merkez susar.
    meta: { handledCodes: ['VALIDATION_ERROR'] },
    onSuccess: (fresh: ActionDetail) => {
      qc.setQueryData(detailKey, fresh)
      qc.invalidateQueries({ queryKey: ['action-transitions', id] })
    },
  })
}

/** Alt aksiyon Sorumlusu (Atanan) değiştir (073) → dönen taze alt aksiyon detayını cache'e yaz. */
export function useSetSubActionAssignee(id: number) {
  const qc = useQueryClient()
  const detailKey = useActionDetailKey(id)
  return useMutation({
    mutationFn: (person: PersonInput) => setSubActionAssignee(id, person),
    onSuccess: (fresh: ActionDetail) => qc.setQueryData(detailKey, fresh),
  })
}

/** Mevcut statüden izin verilen hedef statü id'leri (statü değişince yeniden okunur). */
export function useAllowedTransitions(id: number | null, statusId: number | null | undefined) {
  return useQuery({
    queryKey: ['action-transitions', id, statusId],
    queryFn: () => getAllowedTransitions(id as number),
    enabled: id != null && statusId != null,
  })
}
